import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    Paper,
    Box,
    Typography,
    IconButton,
    TextField,
    Avatar,
    Button,
    Menu,
    MenuItem,
    Divider,
    Tooltip,
    useTheme,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import axios from 'axios';

import './ChatBot.css';

// --- API Configuration ---
const API_URL = "https://ddmschatbot.mssplonline.in/api/chat/ask";
const DEFAULT_SESSION_ID = "session_1234567890";

// --- Interfaces ---
interface Message {
    id: number;
    sender: 'user' | 'bot';
    text: string;
    sourceDocument?: string;
    followUpQuestions?: string[];
}

interface ChatBotProps {
    open: boolean;
    onClose: () => void;
}

// --- Main ChatBot Component ---
const ChatBot: React.FC<ChatBotProps> = ({ open, onClose }) => {
    const theme = useTheme();
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [sessionId, setSessionId] = useState(DEFAULT_SESSION_ID);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);

    // Scroll to bottom effect
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isBotTyping]);

    // Reset messages when dialog opens
    useEffect(() => {
        if (open) {
            setMessages([]);
            setSessionId(DEFAULT_SESSION_ID);
        }
    }, [open]);

    // Handle menu open
    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    // Handle menu close
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Refresh Chat - Clear all messages
    const handleRefreshChat = () => {
        setMessages([]);
        setSessionId(DEFAULT_SESSION_ID);
        handleMenuClose();
    };

    // Clear Chat - Remove all messages
    const handleClearChat = () => {
        setMessages([]);
        setSessionId(DEFAULT_SESSION_ID);
        handleMenuClose();
    };

    // New Session - Generate new session ID and clear chat
    const handleNewSession = () => {
        const newSessionId = `session_${Date.now()}`;
        setSessionId(newSessionId);
        setMessages([]);
        handleMenuClose();
    };

    const handleSendMessage = async (question?: string) => {
        const text = question || inputValue.trim();
        if (!text || isBotTyping) return;

        // Add user message
        const userMessage: Message = {
            id: Date.now(),
            sender: 'user',
            text
        };
        setMessages(prev => [...prev, userMessage]);
        
        if (!question) {
            setInputValue("");
        }
        setIsBotTyping(true);

        try {
            // API Call with current session ID
            const response = await axios.post(API_URL, {
                question: text,
                session_id: sessionId,
            });

            console.log("Full API Response:", response.data); // Debug ke liye

            // Bot response - Poora response directly show karo
            const botResponseMessage: Message = {
                id: Date.now() + 1,
                sender: 'bot',
                text: response.data.answer,  // API ka answer exactly waisa hi
                sourceDocument: response.data.source_document,
                followUpQuestions: response.data.follow_up_questions
            };
            setMessages(prev => [...prev, botResponseMessage]);

        } catch (error) {
            console.error("API call failed:", error);

            const botErrorMessage: Message = {
                id: Date.now() + 1,
                sender: 'bot',
                text: "Sorry, I couldn't process your request. Please try again.",
                sourceDocument: "N/A"
            };
            setMessages(prev => [...prev, botErrorMessage]);
        } finally {
            setIsBotTyping(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                className: 'chatbot-ai-paper',
                sx: {
                    width: '500px',  // Width slightly increased for better readability
                    height: '85vh',
                    maxHeight: '750px',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px',
                    bgcolor: '#282c34',
                    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4)',
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 1,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 20, color: theme.palette.primary.light }} />
                    <Typography variant="subtitle2" sx={{ color: '#ffffff' }}>
                        DMS Assistant
                    </Typography>
                </Box>
                
                <Box>
                    <Tooltip title="Chat Options">
                        <IconButton
                            onClick={handleMenuClick}
                            size="small"
                            sx={{ color: '#a0a0a0', '&:hover': { color: '#ffffff' } }}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Tooltip>
                    
                    <Menu
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleMenuClose}
                        PaperProps={{
                            sx: {
                                bgcolor: '#3a3f4a',
                                color: '#ffffff',
                                mt: 1,
                                '& .MuiMenuItem-root': {
                                    fontSize: '0.9rem',
                                    gap: 1,
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    }
                                }
                            }
                        }}
                    >
                        <MenuItem onClick={handleRefreshChat}>
                            <RefreshIcon fontSize="small" />
                            Refresh Chat
                        </MenuItem>
                        <MenuItem onClick={handleClearChat}>
                            <DeleteSweepIcon fontSize="small" />
                            Clear All Messages
                        </MenuItem>
                        <MenuItem onClick={handleNewSession}>
                            <AutoAwesomeIcon fontSize="small" />
                            New Session
                        </MenuItem>
                        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                        <MenuItem onClick={onClose}>
                            <CloseIcon fontSize="small" />
                            Close Chat
                        </MenuItem>
                    </Menu>
                    
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ color: '#a0a0a0', '&:hover': { color: '#ffffff' }, ml: 0.5 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Messages Area */}
            <Box
                className="messages-area"
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    px: 2,
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {messages.length === 0 ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        mt: 4,
                        color: '#888'
                    }}>
                        <AutoAwesomeIcon sx={{ fontSize: 40, opacity: 0.3, mb: 2 }} />
                        <Typography variant="body2">
                            Ask me anything about your documents...
                        </Typography>
                    </Box>
                ) : (
                    messages.map((msg) => (
                        <MessageItem 
                            key={msg.id} 
                            message={msg} 
                            onFollowUpClick={handleSendMessage}
                        />
                    ))
                )}

                {/* Typing Indicator */}
                {isBotTyping && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        pl: '44px',
                        minHeight: '40px'
                    }}>
                        <Typography variant="body2" sx={{ color: '#aaa', mr: 1, fontSize: '0.8rem' }}>
                            Assistant is typing
                        </Typography>
                        <Box className="typing-dots">
                            <Box component="span" className="typing-dot" />
                            <Box component="span" className="typing-dot" />
                            <Box component="span" className="typing-dot" />
                        </Box>
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Box
                className="input-area-ai"
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    flexShrink: 0,
                }}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Ask me anything about your documents..."
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    disabled={isBotTyping}
                    multiline
                    maxRows={3}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '20px',
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                            color: '#e0e0e0',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                            '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
                        },
                        '& .MuiInputBase-input': {
                            color: '#f0f0f0',
                            '&::placeholder': {
                                color: '#888',
                                opacity: 1
                            },
                        },
                    }}
                />
                
                <Tooltip title="Refresh Chat">
                    <IconButton
                        onClick={handleRefreshChat}
                        disabled={isBotTyping}
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            color: '#a0a0a0',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                color: '#ffffff'
                            }
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
                
                <Tooltip title="Send">
                    <IconButton
                        color="primary"
                        onClick={() => handleSendMessage()}
                        disabled={!inputValue.trim() || isBotTyping}
                        className="send-button-ai"
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark
                            },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                color: '#666'
                            }
                        }}
                    >
                        <SendIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        </Dialog>
    );
};

// --- Message Item Component - FIXED VERSION ---
const MessageItem = ({ message, onFollowUpClick }: { 
    message: Message; 
    onFollowUpClick: (question: string) => void;
}) => {
    const theme = useTheme();
    const isUser = message.sender === 'user';

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                alignItems: 'flex-start',
                width: '100%',
                mb: 3 // थोड़ा गैप बढ़ाया ताकि फॉलो-अप बटन्स अलग दिखें
            }}
        >
            {!isUser && (
                <Avatar sx={{ bgcolor: 'transparent', width: 32, height: 32, mr: 1, border: '1px solid #1976d2', flexShrink: 0 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                </Avatar>
            )}
            
            <Box sx={{ maxWidth: '85%' }}>
                {/* मुख्य मैसेज बबल */}
                <Paper
                    elevation={1}
                    sx={{
                        p: '12px 16px',
                        borderRadius: isUser ? '20px 20px 5px 20px' : '20px 20px 20px 5px',
                        bgcolor: isUser ? '#1565c0' : '#3a3f4a',
                        color: '#ffffff',
                    }}
                >
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            lineHeight: 1.6,
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap', // API का फॉर्मेट सुरक्षित रखने के लिए
                            wordBreak: 'break-word'
                        }}
                    >
                        {message.text}
                    </Typography>

                    {/* Source Document */}
                    {!isUser && message.sourceDocument && message.sourceDocument !== "N/A" && (
                        <Box sx={{ 
                            mt: 1.5, 
                            pt: 1, 
                            borderTop: '1px solid rgba(255,255,255,0.1)', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5 
                        }}>
                            <DescriptionIcon sx={{ fontSize: 14, color: '#b0b0b0' }} />
                            <Typography variant="caption" sx={{ color: '#b0b0b0', fontWeight: 'bold' }}>
                                Source: {message.sourceDocument}
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* Follow-up Questions Section - यहाँ बटन्स दिखेंगे */}
                {!isUser && message.followUpQuestions && message.followUpQuestions.length > 0 && (
                    <Box sx={{ mt: 1.5, ml: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <HelpOutlineIcon sx={{ fontSize: 14, color: '#90caf9' }} />
                            <Typography variant="caption" sx={{ color: '#90caf9', fontWeight: 600 }}>
                                Suggested Questions:
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                            {message.followUpQuestions.map((question, index) => (
                                <Button
                                    key={index}
                                    variant="outlined"
                                    size="small"
                                    // API से आने वाले '**' को हटाने के लिए .replace() का इस्तेमाल
                                    onClick={() => onFollowUpClick(question.replace(/\*\*/g, ''))}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textAlign: 'left',
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        borderColor: 'rgba(144, 202, 249, 0.3)',
                                        color: '#e0e0e0',
                                        fontSize: '0.75rem',
                                        py: 0.8,
                                        px: 1.5,
                                        bgcolor: 'rgba(255, 255, 255, 0.03)',
                                        '&:hover': {
                                            borderColor: '#90caf9',
                                            bgcolor: 'rgba(144, 202, 249, 0.1)',
                                            color: '#ffffff'
                                        }
                                    }}
                                >
                                    {question.replace(/\*\*/g, '')}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            {isUser && (
                <Avatar sx={{ bgcolor: '#666', width: 32, height: 32, ml: 1, flexShrink: 0 }}>
                    <PersonOutlineIcon fontSize="small" />
                </Avatar>
            )}
        </Box>
    );
};

export default ChatBot;